import { audio } from '../../../audio/AudioManager.js';
import { SaveManager } from '../../../state/SaveManager.js';
import { applicationLifecycle } from '../../shared/lifecycle/index.js';
import { ForestTrailInputController } from './components/ForestTrailInputController.js';
import {
  resolveStoryForestLayout,
  STORY_FOREST_LAYOUT_HEIGHT,
  STORY_FOREST_LAYOUT_WIDTH,
} from './components/StoryForestLayout.js';
import { STORY_FOREST_OPENING } from './StoryForestConfig.js';
import {
  hasStoryForestProductionAssets,
  preloadStoryForestAssets,
  STORY_FOREST_ASSET_LIST,
  STORY_FOREST_ASSETS,
} from './StoryForestAssets.js';
import {
  completeStoryTrail,
  getStoryForestState,
  markStoryTreeDiscovered,
  STORY_FOREST_DISTRICTS,
  type StoryForestDistrict,
  visitStoryForestDistrict,
} from './StoryForestState.js';
import { StoryTrail } from './minigames/StoryTrail.js';
import { StoryTrailState } from './minigames/StoryTrailState.js';
import {
  hasWordGardenProductionAssets,
  preloadWordGardenAssets,
  WORD_GARDEN_ASSET_LIST,
  WORD_GARDEN_ASSET_REQUIREMENTS,
} from './minigames/word-garden/WordGardenAssets.js';

const FONT_UI = '"Nunito", Arial, sans-serif';
const COSMO_BASELINE_Y = 610;
const COSMO_WIDTH = 132;
const COSMO_HEIGHT = 176;
const STORY_TREE_X = 860;
const STORY_TREE_Y = 338;
const STORY_TREE_WIDTH = 450;
const STORY_TREE_HEIGHT = 562;
const STORY_LANTERN_X = 1030;
const STORY_LANTERN_Y = 438;
const WORD_GARDEN_PATH_X = 505;
const WORD_GARDEN_PATH_Y = 472;

interface TrailTokenView {
  eventId: string;
  image: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
}

export class StoryForestScene extends Phaser.Scene {
  private requestedDistrict?: StoryForestDistrict;
  private renderScale = 1;
  private reducedMotion = false;
  private returningToHub = false;
  private entryReady = false;
  private discoveryRunning = false;
  private restorationRunning = false;
  private storyTreeDiscovered = false;
  private storyCompleted = false;
  private resizeDebounce?: number;
  private unsubscribeLifecycle?: () => void;
  private background!: Phaser.GameObjects.Image;
  private foliageLeft!: Phaser.GameObjects.Image;
  private foliageRight!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private storyTree!: Phaser.GameObjects.Image;
  private storyLantern!: Phaser.GameObjects.Image;
  private wordGardenPath?: Phaser.GameObjects.Image;
  private wordGardenPathLabel?: Phaser.GameObjects.Text;
  private wordGardenPathHitArea?: Phaser.GameObjects.Zone;
  private homeButton!: Phaser.GameObjects.Container;
  private worldTitle!: Phaser.GameObjects.Text;
  private instructionText!: Phaser.GameObjects.Text;
  private discoveryPanel?: Phaser.GameObjects.Container;
  private trailInput!: ForestTrailInputController;
  private trailState!: StoryTrailState;
  private tokenViews: TrailTokenView[] = [];
  private activeTrail?: StoryTrail;

  constructor() {
    super({ key: 'StoryForest' });
  }

  init(data?: { district?: StoryForestDistrict }) {
    this.requestedDistrict = data?.district;
  }

  preload() {
    preloadStoryForestAssets(this);
    preloadWordGardenAssets(this);
  }

  create() {
    if (!hasStoryForestProductionAssets(this)) {
      this.scene.start('HomeHub');
      return;
    }

    if (this.requestedDistrict) visitStoryForestDistrict(this.requestedDistrict);
    const savedDistrict = getStoryForestState();
    if (savedDistrict.currentDistrict === STORY_FOREST_DISTRICTS.wordGarden
        && savedDistrict.storyTreeStage === 'awakened-1'
        && hasWordGardenProductionAssets(this)) {
      this.scene.start('WordGarden');
      return;
    }

    void audio.enterSoundscape('story-forest');
    this.returningToHub = false;
    this.entryReady = false;
    this.discoveryRunning = false;
    this.restorationRunning = false;
    this.activeTrail = undefined;
    this.discoveryPanel = undefined;
    this.tokenViews = [];
    this.renderScale = this.configureCrispRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    STORY_FOREST_ASSET_LIST.forEach(asset => {
      this.textures.get(asset.key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    });
    WORD_GARDEN_ASSET_LIST.forEach(asset => {
      this.textures.get(asset.key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    });

    const visit = visitStoryForestDistrict(STORY_FOREST_DISTRICTS.silentStoryTree);
    const worldState = getStoryForestState();
    this.storyTreeDiscovered = worldState.storyTreeDiscovered;
    this.storyCompleted = worldState.storyTreeStage === 'awakened-1';
    this.trailState = new StoryTrailState(STORY_FOREST_OPENING.challenge);
    if (this.storyCompleted) this.trailState.collectAll();

    this.buildEnvironment();
    this.buildStoryTree();
    this.buildWordGardenPath(this.storyCompleted);
    this.buildExplorer();
    this.buildTrailTokens();
    this.buildSafeUI();
    this.positionSafeUI();
    this.registerInput();
    this.registerLifecycleSafety();
    this.setupParallax();
    this.applyTextResolution(this.renderScale);
    this.playArrivalSequence(visit.firstVisit);
  }

  private buildEnvironment() {
    this.background = this.add.image(
      STORY_FOREST_LAYOUT_WIDTH * 0.5,
      STORY_FOREST_LAYOUT_HEIGHT * 0.5,
      STORY_FOREST_ASSETS.arrivalBackground.key,
    ).setDepth(0);
    this.coverImage(this.background);
    if (!this.storyCompleted) this.background.setTint(0xdce7df);

    this.foliageLeft = this.add.image(0, 720, STORY_FOREST_ASSETS.foregroundFoliageLeft.key)
      .setOrigin(0, 1).setDepth(90).setDisplaySize(360, 480);
    this.foliageRight = this.add.image(1280, 720, STORY_FOREST_ASSETS.foregroundFoliageRight.key)
      .setOrigin(1, 1).setDepth(90).setDisplaySize(360, 480);
  }

  private buildStoryTree() {
    this.storyTree = this.add.image(
      STORY_TREE_X,
      STORY_TREE_Y,
      this.storyCompleted
        ? STORY_FOREST_ASSETS.storyTreeAwakened.key
        : STORY_FOREST_ASSETS.storyTreeDormant.key,
    ).setDepth(25).setDisplaySize(STORY_TREE_WIDTH, STORY_TREE_HEIGHT);
    if (!this.storyCompleted) this.storyTree.setTint(0x9ba9b1).setAlpha(0.94);

    this.storyLantern = this.add.image(
      STORY_LANTERN_X,
      STORY_LANTERN_Y,
      STORY_FOREST_ASSETS.storyLantern.key,
    ).setDepth(50).setDisplaySize(118, 118).setInteractive({ useHandCursor: true });
    this.storyLantern.setVisible(this.storyTreeDiscovered || this.storyCompleted);
    if (this.storyCompleted) {
      this.storyLantern.setAlpha(1);
    } else {
      this.storyLantern.setTint(0x697782).setAlpha(0.58);
    }
    this.storyLantern.on('pointerup', () => this.tryOpenStoryTrail());
  }

  private buildWordGardenPath(visible: boolean) {
    if (!hasWordGardenProductionAssets(this)) return;
    const asset = WORD_GARDEN_ASSET_REQUIREMENTS.pathOpen;
    this.wordGardenPath = this.add.image(WORD_GARDEN_PATH_X, WORD_GARDEN_PATH_Y, asset.key)
      .setDepth(15)
      .setCrop(7, 7, 406, 337)
      .setDisplaySize(190, 158)
      .setAlpha(0.92);
    this.wordGardenPathLabel = this.add.text(
      WORD_GARDEN_PATH_X,
      WORD_GARDEN_PATH_Y + 86,
      'WORD GARDEN  →',
      {
        fontFamily: FONT_UI,
        fontSize: '17px',
        fontStyle: 'bold',
        color: '#fff4bd',
        stroke: '#244632',
        strokeThickness: 6,
        letterSpacing: 1,
      },
    ).setOrigin(0.5).setDepth(51).setResolution(2);
    this.wordGardenPathHitArea = this.add.zone(
      WORD_GARDEN_PATH_X,
      WORD_GARDEN_PATH_Y,
      236,
      196,
    ).setDepth(50).setInteractive({ useHandCursor: true });
    this.wordGardenPathHitArea.on('pointerup', () => this.enterWordGarden());
    this.setWordGardenPathVisible(visible, false);
  }

  private setWordGardenPathVisible(visible: boolean, animate: boolean) {
    if (!this.wordGardenPath || !this.wordGardenPathLabel || !this.wordGardenPathHitArea) return;
    this.wordGardenPath.setVisible(visible);
    this.wordGardenPathLabel.setVisible(visible);
    this.wordGardenPathHitArea.input!.enabled = visible;
    if (!visible) return;
    if (!animate || this.reducedMotion) {
      this.wordGardenPath.setAlpha(0.96);
      this.wordGardenPathLabel.setAlpha(1);
      return;
    }
    this.wordGardenPath.setAlpha(0);
    this.wordGardenPathLabel.setAlpha(0);
    this.tweens.add({
      targets: [this.wordGardenPath, this.wordGardenPathLabel],
      alpha: 1,
      duration: 620,
      ease: 'Sine.easeOut',
    });
  }

  private buildExplorer() {
    this.cosmo = this.add.image(220, COSMO_BASELINE_Y, STORY_FOREST_ASSETS.storyForestCosmo.key)
      .setDepth(55).setDisplaySize(COSMO_WIDTH, COSMO_HEIGHT).setAlpha(0);
    this.trailInput = new ForestTrailInputController(this, this.cosmo, {
      minX: 120,
      maxX: 1080,
      baselineY: COSMO_BASELINE_Y,
      depth: 40,
      onPositionChanged: x => this.handleExplorerPosition(x),
    });
  }

  private buildTrailTokens() {
    const positions = [
      { x: 405, y: 555 },
      { x: 575, y: 515 },
      { x: 735, y: 555 },
    ];
    STORY_FOREST_OPENING.challenge.events.forEach((event, index) => {
      if (this.storyCompleted) return;
      const position = positions[index] ?? { x: 405 + index * 160, y: 545 };
      const image = this.add.image(
        position.x,
        position.y,
        STORY_FOREST_ASSETS.storyTrailMarker.key,
      ).setDepth(52).setDisplaySize(82, 82).setInteractive({ useHandCursor: true });
      const label = this.add.text(position.x, position.y, `${index + 1}`, {
        fontFamily: FONT_UI,
        fontSize: '19px',
        fontStyle: 'bold',
        color: '#fff8df',
        stroke: '#45205f',
        strokeThickness: 5,
      }).setOrigin(0.5).setDepth(53).setResolution(2);
      image.on('pointerup', () => this.collectTrailToken(event.id));
      image.setVisible(this.storyTreeDiscovered).setAlpha(this.storyTreeDiscovered ? 1 : 0);
      label.setVisible(this.storyTreeDiscovered).setAlpha(this.storyTreeDiscovered ? 1 : 0);
      this.tokenViews.push({ eventId: event.id, image, label });
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: [image, label],
          y: position.y - 6,
          yoyo: true,
          repeat: -1,
          duration: 1500 + index * 170,
          ease: 'Sine.easeInOut',
        });
      }
    });
  }

  private buildSafeUI() {
    this.worldTitle = this.add.text(640, 52, 'STORY FOREST', {
      fontFamily: FONT_UI,
      fontSize: '30px',
      fontStyle: 'bold',
      color: '#fff2b8',
      stroke: '#274c38',
      strokeThickness: 7,
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(101).setAlpha(0).setResolution(2);

    const homeText = this.add.text(0, 0, '←  HOME HUB', {
      fontFamily: FONT_UI,
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#fff7d6',
      stroke: '#163323',
      strokeThickness: 7,
      shadow: { offsetX: 0, offsetY: 3, blur: 4, color: '#07170e', fill: true },
    }).setOrigin(0.5).setResolution(2);
    const homeHitArea = this.add.zone(0, 0, 150, 50).setInteractive({ useHandCursor: true });
    homeHitArea.on('pointerdown', () => homeText.setScale(0.96).setAlpha(0.78));
    homeHitArea.on('pointerout', () => homeText.setScale(1).setAlpha(1));
    homeHitArea.on('pointerup', () => {
      homeText.setScale(1).setAlpha(1);
      this.returnToHub();
    });
    this.homeButton = this.add.container(0, 0, [homeHitArea, homeText]).setDepth(101).setAlpha(0);

    this.instructionText = this.add.text(640, 112, '', {
      fontFamily: FONT_UI,
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#fff8dc',
      align: 'center',
      stroke: '#163323',
      strokeThickness: 8,
      shadow: { offsetX: 0, offsetY: 3, blur: 5, color: '#07170e', fill: true },
      wordWrap: { width: 760 },
      lineSpacing: 3,
    }).setOrigin(0.5).setDepth(101).setAlpha(0).setResolution(2);
  }

  private playArrivalSequence(firstVisit: boolean) {
    this.trailInput.disable();
    this.cameras.main.fadeIn(this.reducedMotion ? 180 : 520, 243, 255, 231);
    this.worldTitle.y -= this.reducedMotion ? 0 : 10;
    this.tweens.add({
      targets: this.worldTitle,
      y: this.worldTitle.y + (this.reducedMotion ? 0 : 10),
      alpha: 1,
      duration: this.reducedMotion ? 180 : 650,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.cosmo,
      alpha: 1,
      x: 240,
      duration: this.reducedMotion ? 220 : 850,
      delay: this.reducedMotion ? 80 : 380,
      ease: 'Sine.easeOut',
    });
    this.instructionText.setText(
      firstVisit && !this.storyTreeDiscovered
        ? 'A new path! Explore the clearing and find out why the great tree is quiet.'
        : this.currentInstruction(),
    );
    this.tweens.add({
      targets: [this.homeButton, this.instructionText],
      alpha: 1,
      duration: this.reducedMotion ? 180 : 480,
      delay: this.reducedMotion ? 180 : 1050,
      ease: 'Sine.easeOut',
    });
    this.time.delayedCall(this.reducedMotion ? 480 : 1720, () => {
      this.entryReady = true;
      if (document.hasFocus()) this.trailInput.enable();
      this.instructionText.setText(this.currentInstruction());
    });
  }

  private registerInput() {
    this.input.keyboard?.on('keydown-E', this.handleInteract, this);
    this.input.keyboard?.on('keydown-SPACE', this.handleInteract, this);
    this.input.keyboard?.on('keydown-ESC', this.handleEscape, this);
  }

  private handleInteract() {
    if (this.activeTrail || !this.entryReady || this.returningToHub
        || this.discoveryRunning || this.restorationRunning) return;
    const nearbyToken = this.tokenViews.find(view =>
      view.image.visible && Math.abs(view.image.x - this.cosmo.x) <= 105,
    );
    if (nearbyToken) {
      this.collectTrailToken(nearbyToken.eventId);
      return;
    }
    if (this.storyCompleted && this.wordGardenPath?.visible
        && Math.abs(WORD_GARDEN_PATH_X - this.cosmo.x) <= 145) {
      this.enterWordGarden();
      return;
    }
    if (Math.abs(this.storyLantern.x - this.cosmo.x) <= 145) this.tryOpenStoryTrail();
  }

  private handleEscape() {
    if (this.discoveryRunning || this.restorationRunning) return;
    if (this.activeTrail) {
      this.closeStoryTrail();
      return;
    }
    this.returnToHub();
  }

  private handleExplorerPosition(x: number) {
    if (!this.entryReady || this.discoveryRunning || this.restorationRunning || this.activeTrail) return;
    if (!this.storyTreeDiscovered && x >= 690) {
      this.beginStoryTreeDiscovery();
      return;
    }
    this.updateNearbyPrompt();
  }

  private enterWordGarden() {
    if (!this.entryReady || this.returningToHub || this.discoveryRunning
        || this.restorationRunning || this.activeTrail || !this.storyCompleted
        || !hasWordGardenProductionAssets(this)) return;
    this.entryReady = false;
    this.trailInput.disable();
    this.cameras.main.fadeOut(this.reducedMotion ? 170 : 420, 231, 255, 224);
    this.time.delayedCall(this.reducedMotion ? 190 : 450, () => this.scene.start('WordGarden'));
  }

  private beginStoryTreeDiscovery() {
    if (this.storyTreeDiscovered || this.discoveryRunning) return;
    this.discoveryRunning = true;
    this.entryReady = false;
    this.trailInput.disable();
    this.storyTreeDiscovered = true;
    markStoryTreeDiscovered();
    this.storyLantern.setVisible(true).setAlpha(0);
    this.setTrailTokensVisible(true, true);
    this.instructionText.setText('Cosmo found the Silent Story Tree!');

    if (!this.reducedMotion) {
      this.cameras.main.zoomTo(this.renderScale * 1.045, 620, 'Sine.easeInOut');
      this.cameras.main.pan(665, STORY_TREE_Y, 620, 'Sine.easeInOut');
      this.tweens.add({
        targets: this.storyTree,
        scaleX: this.storyTree.scaleX * 1.025,
        scaleY: this.storyTree.scaleY * 1.025,
        duration: 520,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: this.cosmo,
        angle: -4,
        duration: 240,
        yoyo: true,
        repeat: 1,
      });
    }
    this.tweens.add({
      targets: this.storyLantern,
      alpha: 0.58,
      duration: this.reducedMotion ? 180 : 520,
    });

    this.time.delayedCall(this.reducedMotion ? 220 : 760, () => this.showDiscoveryPanel());
    this.time.delayedCall(this.reducedMotion ? 1500 : 3550, () => {
      if (this.discoveryPanel) {
        this.tweens.add({
          targets: this.discoveryPanel,
          alpha: 0,
          duration: this.reducedMotion ? 160 : 360,
        });
      }
    });
    this.time.delayedCall(this.reducedMotion ? 1720 : 3980, () => {
      if (!this.reducedMotion) {
        this.cameras.main.zoomTo(this.renderScale, 480, 'Sine.easeInOut');
        this.cameras.main.pan(
          STORY_FOREST_LAYOUT_WIDTH * 0.5,
          STORY_FOREST_LAYOUT_HEIGHT * 0.5,
          480,
          'Sine.easeInOut',
        );
      }
    });
    this.time.delayedCall(this.reducedMotion ? 1900 : 4500, () => {
      this.discoveryPanel?.destroy(true);
      this.discoveryPanel = undefined;
      this.discoveryRunning = false;
      this.entryReady = true;
      this.instructionText.setText(this.currentInstruction());
      if (document.hasFocus()) this.trailInput.enable();
    });
  }

  private showDiscoveryPanel() {
    const panel = this.add.image(0, 0, STORY_FOREST_ASSETS.storyMissionPanel.key)
      .setDisplaySize(660, 495);
    const title = this.add.text(0, -82, 'THE SILENT STORY TREE', {
      fontFamily: FONT_UI,
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#54246f',
      align: 'center',
    }).setOrigin(0.5).setResolution(2);
    const body = this.add.text(
      0,
      2,
      'The Story Tree has gone quiet. Three moments from a story are scattered along the path. Find them, then restore their order so the first Story Lantern can shine.',
      {
        fontFamily: FONT_UI,
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#3d2a31',
        align: 'center',
        lineSpacing: 7,
        wordWrap: { width: 445 },
      },
    ).setOrigin(0.5).setResolution(2);
    this.discoveryPanel = this.add.container(640, 365, [panel, title, body])
      .setDepth(180).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.96);
    this.layoutDiscoveryPanel();
    this.tweens.add({
      targets: this.discoveryPanel,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 180 : 420,
      ease: 'Sine.easeOut',
    });
  }

  private layoutDiscoveryPanel() {
    if (!this.discoveryPanel) return;
    const { safe, compact } = resolveStoryForestLayout(this);
    this.discoveryPanel.setPosition(safe.centerX, safe.centerY + (compact ? 6 : 16));
    const panel = this.discoveryPanel.list[0] as Phaser.GameObjects.Image;
    const title = this.discoveryPanel.list[1] as Phaser.GameObjects.Text;
    const body = this.discoveryPanel.list[2] as Phaser.GameObjects.Text;
    panel.setDisplaySize(compact ? 600 : 660, compact ? 450 : 495);
    title.setPosition(0, compact ? -75 : -82).setFontSize(compact ? 21 : 24);
    body.setFontSize(compact ? 18 : 20).setWordWrapWidth(compact ? 410 : 445);
  }

  private setTrailTokensVisible(visible: boolean, animate: boolean) {
    this.tokenViews.forEach((view, index) => {
      view.image.setVisible(visible);
      view.label.setVisible(visible);
      if (!visible) return;
      if (!animate || this.reducedMotion) {
        view.image.setAlpha(1);
        view.label.setAlpha(1);
        return;
      }
      view.image.setAlpha(0);
      view.label.setAlpha(0);
      this.tweens.add({
        targets: [view.image, view.label],
        alpha: 1,
        duration: 380,
        delay: 900 + index * 150,
        ease: 'Sine.easeOut',
      });
    });
  }

  private collectTrailToken(eventId: string) {
    if (!this.storyTreeDiscovered || !this.entryReady || this.activeTrail) return;
    if (!this.trailState.collect(eventId)) return;
    const view = this.tokenViews.find(token => token.eventId === eventId);
    if (view) {
      this.tweens.killTweensOf([view.image, view.label]);
      const magic = this.add.image(view.image.x, view.image.y, STORY_FOREST_ASSETS.storyMagicVfx.key)
        .setDepth(54).setDisplaySize(130, 130).setAlpha(0);
      this.tweens.add({
        targets: magic,
        alpha: 0.9,
        duration: this.reducedMotion ? 100 : 220,
        yoyo: true,
        onComplete: () => magic.destroy(),
      });
      if (this.reducedMotion) {
        view.image.setVisible(false);
        view.label.setVisible(false);
      } else {
        this.tweens.add({
          targets: [view.image, view.label],
          alpha: 0,
          scale: 0.72,
          duration: 260,
          onComplete: () => {
            view.image.setVisible(false);
            view.label.setVisible(false);
          },
        });
      }
    }
    this.instructionText.setText(this.currentInstruction());
    if (this.trailState.allEventsCollected) {
      this.storyLantern.clearTint().setAlpha(0.82);
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: this.storyLantern,
          scaleX: this.storyLantern.scaleX * 1.08,
          scaleY: this.storyLantern.scaleY * 1.08,
          yoyo: true,
          repeat: 1,
          duration: 320,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  private currentInstruction() {
    if (this.storyCompleted) {
      const state = getStoryForestState();
      return state.completedWordGardenChallenges.includes('word-garden-context-1')
        ? 'The Story Tree is awake, and the restored Word Garden blooms beyond the open path.'
        : 'The Story Tree is awake! A new path leads to the Word Garden.';
    }
    if (!this.storyTreeDiscovered) {
      return 'Explore the clearing and follow the path to the great Story Tree.';
    }
    const found = this.trailState.collectedEventIds.length;
    if (this.trailState.allEventsCollected) {
      return 'All story moments found! Tap the lantern to restore their order.';
    }
    return `${found} of ${STORY_FOREST_OPENING.challenge.events.length} story moments found along the path.`;
  }

  private tryOpenStoryTrail() {
    if (this.activeTrail || !this.entryReady || !this.storyTreeDiscovered
        || !this.trailState.allEventsCollected || this.returningToHub
        || this.discoveryRunning || this.restorationRunning || this.storyCompleted) return;
    this.entryReady = false;
    this.trailInput.disable();
    this.homeButton.setVisible(false);
    this.worldTitle.setVisible(false);
    this.instructionText.setVisible(false);
    this.activeTrail = new StoryTrail(this, STORY_FOREST_OPENING.challenge, {
      state: this.trailState,
      reducedMotion: this.reducedMotion,
      onExit: () => this.closeStoryTrail(),
      onComplete: config => this.finishStoryTrail(config.id, config.rewardEvent),
    });
  }

  private closeStoryTrail(resumeExploration = true) {
    this.activeTrail?.destroy();
    this.activeTrail = undefined;
    this.homeButton.setVisible(true);
    this.worldTitle.setVisible(true);
    this.instructionText.setVisible(true);
    if (!resumeExploration || this.returningToHub) return;
    this.entryReady = true;
    this.instructionText.setText(this.currentInstruction());
    if (document.hasFocus()) this.trailInput.enable();
  }

  private finishStoryTrail(challengeId: string, rewardEvent: string) {
    const result = completeStoryTrail(challengeId, rewardEvent, [STORY_FOREST_DISTRICTS.wordGarden]);
    this.storyCompleted = true;
    this.closeStoryTrail(false);
    if (!result.firstCompletion) {
      this.storyTree.setTexture(STORY_FOREST_ASSETS.storyTreeAwakened.key)
        .clearTint().setAlpha(1).setDisplaySize(STORY_TREE_WIDTH, STORY_TREE_HEIGHT);
      this.storyLantern.clearTint().setAlpha(1);
      this.setWordGardenPathVisible(true, false);
      this.entryReady = true;
      this.trailInput.enable();
      return;
    }
    this.beginRestorationSequence();
  }

  private beginRestorationSequence() {
    this.restorationRunning = true;
    this.entryReady = false;
    this.trailInput.disable();
    this.instructionText.setText('Your story is waking the forest!');
    const magic = this.add.image(STORY_TREE_X, STORY_TREE_Y, STORY_FOREST_ASSETS.storyMagicVfx.key)
      .setDepth(80).setDisplaySize(500, 500).setAlpha(0);

    if (!this.reducedMotion) {
      this.cameras.main.zoomTo(this.renderScale * 1.05, 600, 'Sine.easeInOut');
      this.cameras.main.pan(665, STORY_TREE_Y, 600, 'Sine.easeInOut');
    }
    this.tweens.add({
      targets: magic,
      alpha: 0.95,
      duration: this.reducedMotion ? 180 : 560,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.storyLantern,
      alpha: 1,
      scaleX: this.storyLantern.scaleX * (this.reducedMotion ? 1.02 : 1.15),
      scaleY: this.storyLantern.scaleY * (this.reducedMotion ? 1.02 : 1.15),
      duration: this.reducedMotion ? 180 : 480,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
    });
    this.storyLantern.clearTint();

    this.time.delayedCall(this.reducedMotion ? 260 : 760, () => {
      this.tweens.add({
        targets: this.storyTree,
        alpha: 0,
        duration: this.reducedMotion ? 140 : 360,
        onComplete: () => {
          this.storyTree.setTexture(STORY_FOREST_ASSETS.storyTreeAwakened.key)
            .clearTint().setDisplaySize(STORY_TREE_WIDTH, STORY_TREE_HEIGHT);
          this.background.clearTint();
          this.tweens.add({
            targets: this.storyTree,
            alpha: 1,
            duration: this.reducedMotion ? 180 : 620,
            ease: 'Sine.easeOut',
          });
        },
      });
    });

    this.time.delayedCall(this.reducedMotion ? 620 : 1500, () => {
      this.instructionText.setText(STORY_FOREST_OPENING.challenge.successMessage);
      this.setWordGardenPathVisible(true, true);
      this.tweens.add({
        targets: [this.foliageLeft, this.foliageRight],
        alpha: 0.72,
        duration: this.reducedMotion ? 160 : 420,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: this.cosmo,
          angle: -5,
          scaleX: this.cosmo.scaleX * 1.08,
          scaleY: this.cosmo.scaleY * 1.08,
          duration: 260,
          yoyo: true,
          repeat: 2,
          ease: 'Sine.easeInOut',
        });
      }
    });

    this.time.delayedCall(this.reducedMotion ? 1100 : 2800, () => {
      this.tweens.add({
        targets: magic,
        alpha: 0,
        duration: this.reducedMotion ? 140 : 420,
        onComplete: () => magic.destroy(),
      });
      if (!this.reducedMotion) {
        this.cameras.main.zoomTo(this.renderScale, 480, 'Sine.easeInOut');
        this.cameras.main.pan(
          STORY_FOREST_LAYOUT_WIDTH * 0.5,
          STORY_FOREST_LAYOUT_HEIGHT * 0.5,
          480,
          'Sine.easeInOut',
        );
      }
    });
    this.time.delayedCall(this.reducedMotion ? 1350 : 3380, () => {
      this.restorationRunning = false;
      this.entryReady = true;
      this.instructionText.setText(this.currentInstruction());
      if (document.hasFocus()) this.trailInput.enable();
    });
  }

  private updateNearbyPrompt() {
    let prompt = this.currentInstruction();
    const nearbyToken = this.tokenViews.some(view =>
      view.image.visible && Math.abs(view.image.x - this.cosmo.x) <= 105,
    );
    if (nearbyToken) {
      prompt = 'Press E or Space to collect this story moment.';
    } else if (this.storyCompleted && this.wordGardenPath?.visible
        && Math.abs(WORD_GARDEN_PATH_X - this.cosmo.x) <= 145) {
      prompt = 'Press E or Space to follow the path into the Word Garden.';
    } else if (this.trailState.allEventsCollected
        && Math.abs(this.storyLantern.x - this.cosmo.x) <= 145) {
      prompt = 'Press E or Space to open the Story Trail.';
    }
    if (this.instructionText.text !== prompt) this.instructionText.setText(prompt);
  }

  private setupParallax() {
    if (this.reducedMotion) return;
    this.input.on('pointermove', this.handleParallax, this);
  }

  private handleParallax(pointer: Phaser.Input.Pointer) {
    if (this.activeTrail || this.discoveryRunning || this.restorationRunning) return;
    const normalizedX = pointer.worldX / STORY_FOREST_LAYOUT_WIDTH - 0.5;
    const normalizedY = pointer.worldY / STORY_FOREST_LAYOUT_HEIGHT - 0.5;
    this.foliageLeft.setPosition(normalizedX * -5, 720 + normalizedY * -3);
    this.foliageRight.setPosition(1280 + normalizedX * -7, 720 + normalizedY * -4);
  }

  private configureCrispRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(
      parentW / STORY_FOREST_LAYOUT_WIDTH,
      parentH / STORY_FOREST_LAYOUT_HEIGHT,
    );
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const renderScale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT;
    this.scale.autoCenter = Phaser.Scale.CENTER_BOTH;
    this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(
      Math.round(STORY_FOREST_LAYOUT_WIDTH * renderScale),
      Math.round(STORY_FOREST_LAYOUT_HEIGHT * renderScale),
    );
    this.scale.refresh();
    this.cameras.main.setZoom(renderScale).centerOn(
      STORY_FOREST_LAYOUT_WIDTH * 0.5,
      STORY_FOREST_LAYOUT_HEIGHT * 0.5,
    );
    return renderScale;
  }

  private coverImage(image: Phaser.GameObjects.Image) {
    const scale = Math.max(
      STORY_FOREST_LAYOUT_WIDTH / image.width,
      STORY_FOREST_LAYOUT_HEIGHT / image.height,
    );
    image.setScale(scale);
    return image;
  }

  private applyTextResolution(resolution: number) {
    const visit = (gameObject: Phaser.GameObjects.GameObject) => {
      if (gameObject instanceof Phaser.GameObjects.Text) {
        gameObject.setResolution(resolution);
      } else if (gameObject instanceof Phaser.GameObjects.Container) {
        gameObject.list.forEach(child => visit(child));
      }
    };
    this.children.list.forEach(gameObject => visit(gameObject));
  }

  private positionSafeUI() {
    const { safe, compact } = resolveStoryForestLayout(this);
    this.homeButton.setPosition(safe.x + 78, safe.y + (compact ? 30 : 38));
    this.worldTitle.setPosition(safe.centerX, safe.y + (compact ? 34 : 48))
      .setFontSize(compact ? 25 : 30);
    this.instructionText.setPosition(safe.centerX, safe.y + (compact ? 88 : 108))
      .setFontSize(compact ? 16 : 18)
      .setWordWrapWidth(Math.min(780, safe.width - 210));
  }

  private registerLifecycleSafety() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.handlePause(),
      onResume: () => this.handleResume(),
    });
    window.addEventListener('resize', this.handleWindowResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeLifecycle?.();
      this.unsubscribeLifecycle = undefined;
      audio.leaveSoundscape({ id: 'story-forest' });
      window.removeEventListener('resize', this.handleWindowResize);
      this.input.off('pointermove', this.handleParallax, this);
      if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
      this.input.keyboard?.off('keydown-E', this.handleInteract, this);
      this.input.keyboard?.off('keydown-SPACE', this.handleInteract, this);
      this.input.keyboard?.off('keydown-ESC', this.handleEscape, this);
      this.activeTrail?.destroy();
      this.activeTrail = undefined;
      this.discoveryPanel?.destroy(true);
      this.discoveryPanel = undefined;
      this.trailInput.destroy();
      this.tweens.killAll();
    });
  }

  private handlePause() {
    this.trailInput.disable();
    this.activeTrail?.setSuspended(true);
  }

  private handleResume() {
    if (this.activeTrail) {
      this.activeTrail.setSuspended(false);
      return;
    }
    if (this.entryReady && !this.returningToHub
        && !this.discoveryRunning && !this.restorationRunning) {
      this.trailInput.enable();
    }
  }

  private readonly handleWindowResize = () => {
    if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
    this.resizeDebounce = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureCrispRenderer();
      this.positionSafeUI();
      this.layoutDiscoveryPanel();
      this.activeTrail?.layout();
      this.applyTextResolution(this.renderScale);
    }, 80);
  };

  private returnToHub() {
    if (this.returningToHub || this.discoveryRunning || this.restorationRunning) return;
    this.returningToHub = true;
    this.entryReady = false;
    this.trailInput.disable();
    this.activeTrail?.destroy();
    this.activeTrail = undefined;
    this.cameras.main.fadeOut(this.reducedMotion ? 170 : 420, 243, 255, 231);
    this.time.delayedCall(this.reducedMotion ? 190 : 450, () => this.scene.start('HomeHub'));
  }
}
